/**
 * The add-source form's schemas, one per source category. Each starts from the generated `IntelSourceCreate`
 * schema (so the server's limits apply) and adds the fields only the form has (pasted lists, chain filters).
 */
import { z } from 'zod';
import { zIntelSourceCreate } from '../../../generated/api/zod.gen.js';
import type { IntelSourceCategory } from '../../../types/intel-source.js';
import { getInitialFormData, type FormData } from './source-payloads.js';

const required = zIntelSourceCreate.shape.handle;
const lines = (text: string | undefined): string[] =>
  (text ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

function isValidAddress(addr: string): boolean {
  if (addr.startsWith('0x')) return /^0x[a-fA-F0-9]{40}$/.test(addr);
  return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(addr);
}

const social = zIntelSourceCreate
  .pick({ handle: true })
  .partial()
  .extend({ bulkHandles: z.string().optional() })
  .refine((v) => !!(v.handle?.trim() || v.bulkHandles?.trim()), {
    message: 'Enter a handle, or paste several below',
    path: ['handle'],
  });

const onChain = zIntelSourceCreate
  .pick({ chain: true })
  .extend({
    chain: required,
    address: z.string().optional(),
    bulkAddresses: z.string().optional(),
    label: z.string().optional(),
    tags: z.array(z.string()),
  })
  .superRefine((v, ctx) => {
    const single = v.address?.trim();
    if (!single && lines(v.bulkAddresses).length === 0) {
      ctx.addIssue({ code: 'custom', path: ['address'], message: 'Enter an address, or paste several below' });
    }
    if (single && !isValidAddress(single)) {
      ctx.addIssue({ code: 'custom', path: ['address'], message: 'Enter a valid address' });
    }
    if (lines(v.bulkAddresses).some((addr) => !isValidAddress(addr))) {
      ctx.addIssue({ code: 'custom', path: ['bulkAddresses'], message: 'Every line must be a valid address' });
    }
  });

// An empty object schema derived from the generated one: the base for categories whose fields are all form-only.
const formOnly = zIntelSourceCreate.pick({ handle: true }).omit({ handle: true });

const defi = formOnly.extend({ protocolName: required, chainFilters: z.array(z.string()) });

const institutional = formOnly.extend({
  entityName: required,
  ticker: z.string().optional(),
  entityType: required,
});

const researchLike = formOnly.extend({ sourceName: required, apiUrl: z.url().optional() });

export const SOURCE_FORM_SCHEMAS = {
  social,
  on_chain: onChain,
  defi,
  institutional,
  research: researchLike,
  market_infra: researchLike,
} as const satisfies Record<IntelSourceCategory, z.ZodType>;

const INITIAL = getInitialFormData();

/** Each category's starting values: only the fields its form shows. */
export const SOURCE_FORM_DEFAULTS: Record<IntelSourceCategory, Partial<FormData>> = {
  social: { handle: INITIAL.handle, bulkHandles: INITIAL.bulkHandles },
  on_chain: {
    address: INITIAL.address,
    bulkAddresses: INITIAL.bulkAddresses,
    chain: INITIAL.chain,
    label: INITIAL.label,
    tags: INITIAL.tags,
  },
  defi: { protocolName: INITIAL.protocolName, chainFilters: INITIAL.chainFilters },
  institutional: { entityName: INITIAL.entityName, ticker: INITIAL.ticker, entityType: INITIAL.entityType },
  research: { sourceName: INITIAL.sourceName, apiUrl: INITIAL.apiUrl },
  market_infra: { sourceName: INITIAL.sourceName, apiUrl: INITIAL.apiUrl },
};
